import React, { FC, ReactElement, ReactNode } from 'react';
import { Badge, Button, Flex, FlexProps, Input } from 'antd';
import styled from 'styled-components';
import * as Icon from 'react-feather';

import { i18nNsFactory } from '@atlas-frontend/i18n';
import { colorPrimary } from '@atlas-frontend/design-tokens';
import { Padding } from './Padding';

import translations from './DirectoryHeader.translations.json';
import { BookmarkCreate } from './BookmarkCreate';
import { InfoIcon } from './InfoIcon';
import { InfoIconUrl } from './InfoIconUrl';

const { addTranslations, useTranslation } = i18nNsFactory('detail-layout');
addTranslations(translations);

type Fn = () => void;

type Props = {
  title: string | null;
  onFilterClear?: Fn;
  onSearch?: (val: string) => void;
  filtersCount?: number;
  isFiltersOpen?: boolean;
  onFilterToggle?: Fn;
  defaultValue?: string;
  bookmarkCreate?: ReactElement;
  infoIconUrl?: InfoIconUrl;
  extraActions?: ReactNode;
  justifyExtraActions?: FlexProps['justify'];
};

export const DirectoryHeader: FC<Props> = ({
  title,
  isFiltersOpen,
  filtersCount,
  onFilterClear,
  onFilterToggle,
  onSearch,
  defaultValue,
  bookmarkCreate,
  infoIconUrl,
  extraActions,
  justifyExtraActions = 'space-between',
}) => {
  const { t } = useTranslation();
  const [searchValue, setSearchValue] = React.useState<string | undefined>(
    defaultValue,
  );

  return (
    <DirectoryHeaderPadding horizontal top>
      <Flex gap="small" align="center" justify={justifyExtraActions}>
        <Flex gap="small" align="center" justify="space-between">
          <Title>{title}</Title>
          {infoIconUrl && <InfoIcon infoIconUrl={infoIconUrl}></InfoIcon>}
          {bookmarkCreate ?? <BookmarkCreate />}
        </Flex>
        <Flex gap="small">
          {!!onSearch && (
            <Search
              value={searchValue}
              onChange={(e) => {
                setSearchValue(e.target.value);
              }}
              id="directory-search"
              placeholder={t('search.placeholder')}
              onSearch={onSearch}
              allowClear
              onKeyDown={(event) => {
                if (event.key === 'Escape') {
                  if (onFilterClear) onFilterClear();
                  setSearchValue(undefined);
                }
              }}
            />
          )}
          {(!!onFilterToggle || !!filtersCount) && (
            <>
              {!!onFilterToggle && (
                <Button
                  id="toggle-directory-filter"
                  type="primary"
                  onClick={onFilterToggle}
                >
                  <Flex gap="small" align="center">
                    <Icon.Filter size={14} />
                    {t(
                      isFiltersOpen
                        ? 'button.close-filter'
                        : 'button.open-filter',
                    )}
                  </Flex>
                </Button>
              )}
              {!!filtersCount && !!onFilterClear && (
                <Button id="clear-directory-filter" onClick={onFilterClear}>
                  <Flex gap="small" align="center">
                    {t('button.clear-filter')}
                    <Badge count={filtersCount} color={colorPrimary} />
                  </Flex>
                </Button>
              )}
            </>
          )}
          {extraActions}
        </Flex>
      </Flex>
    </DirectoryHeaderPadding>
  );
};

export const DirectoryHeaderPadding = styled(Padding)`
  position: sticky;
  left: 50%;
  transform: translateX(-50%);
  margin-inline: -999999px;
  width: 100%;
`;

const Search = styled(Input.Search)`
  width: auto;
`;

const Title = styled.h1`
  align-self: flex-start;
`;
